import React, { useMemo } from 'react';
import {
  FolderOpen,
  X,
  Check,
  Loader2,
  Plane,
  Building,
  UtensilsCrossed,
  Handshake,
  Calendar as CalendarIcon,
  Car,
  StickyNote
} from 'lucide-react';

const COMPONENT_ICONS = {
  commercial_flight: Plane,
  private_flight: Plane,
  flight: Plane,
  hotel: Building,
  restaurant: UtensilsCrossed,
  meeting: Handshake,
  event: CalendarIcon,
  rental_car: Car,
  car_rental: Car,
  chauffeur_service: Car,
  memo: StickyNote,
};

const BundleSelectionSummary = ({
  bundle,
  selectedIds,
  tripComponents,
  onRemove,
  onSave,
  onCancel,
  hasChanges,
  isSaving = false,
}) => {
  const selectedComponents = useMemo(() => {
    if (!tripComponents || !selectedIds) return [];
    return [...selectedIds]
      .map(id => tripComponents.find(c => c.id === id))
      .filter(Boolean);
  }, [tripComponents, selectedIds]);

  return (
    <div className="h-full flex flex-col rounded-xl border bg-ui-surface border-ui-line">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ui-divider">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className="w-4 h-4 flex-shrink-0 text-ui-violet-fg" />
          <span className="text-sm font-semibold truncate text-ui-ink">
            {bundle?.title || 'Bundle'}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-ui-violet-bg text-ui-violet-fg">
            {selectedIds.size}
          </span>
        </div>
        <button
          onClick={onCancel}
          className="p-1 rounded transition-colors text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {selectedComponents.length === 0 ? (
          <div className="text-center py-8 text-ui-muted">
            <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No items in bundle</p>
            <p className="text-xs mt-1">Click components on the timeline to add them</p>
          </div>
        ) : (
          selectedComponents.map(comp => {
            const Icon = COMPONENT_ICONS[comp.component_type] || CalendarIcon;
            return (
              <div
                key={comp.id}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors bg-ui-hover border-ui-line"
              >
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-ui-line">
                  <Icon className="w-3.5 h-3.5 text-ui-ink-2" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate text-ui-ink">
                    {comp.title}
                  </div>
                </div>
                <button
                  onClick={() => onRemove(comp.id)}
                  className="p-1 rounded transition-colors flex-shrink-0 text-ui-muted hover:text-ui-red-fg hover:bg-ui-line"
                  title="Remove from bundle"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-ui-divider">
        <span className="text-xs text-ui-muted">
          {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}
          {hasChanges && <span className="text-ui-violet-fg"> (modified)</span>}
        </span>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors text-ui-ink-2 border-ui-control hover:bg-ui-hover"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving || !hasChanges}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ui-on-primary bg-ui-violet-strong rounded-lg hover:bg-ui-violet-strong/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleSelectionSummary;
